import React, { useState } from 'react';
import { Shield, Key, AlertTriangle, RefreshCw, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import api from '../utils/api';

const MFAVerify = ({ mfaToken, rememberMe = false, onSuccess, onCancel }) => {
  const [mode, setMode] = useState('totp'); // 'totp' or 'backup'
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);

  const verifyTOTP = async () => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await api.post('/api/auth/mfa/verify', {
        mfa_token: mfaToken,
        code: code,
        trust_device: trustDevice,
        device_name: trustDevice ? `${navigator.userAgent.substring(0, 50)}` : null,
        remember_me: rememberMe
      });

      // Store trusted device token if provided
      if (response.data.trusted_device_token) {
        localStorage.setItem('trusted_device_token', response.data.trusted_device_token);
      }

      onSuccess(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const verifyBackupCode = async () => {
    const cleanCode = code.replace(/[^A-Za-z0-9-]/g, '').toUpperCase();
    if (cleanCode.length < 8) {
      setError('Please enter a valid backup code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await api.post('/api/auth/mfa/verify-backup', {
        mfa_token: mfaToken,
        backup_code: cleanCode,
        remember_me: rememberMe
      });

      onSuccess(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid backup code');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mode === 'totp') {
      verifyTOTP();
    } else {
      verifyBackupCode();
    }
  };

  const handleCodeChange = (e) => {
    if (mode === 'totp') {
      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
      setCode(value);
    } else {
      setCode(e.target.value.toUpperCase());
    }
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/10 backdrop-blur-lg border-white/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Shield className="w-6 h-6 text-purple-300" />
            </div>
            <div>
              <CardTitle className="text-white">Two-Factor Verification</CardTitle>
              <CardDescription className="text-gray-400">
                {mode === 'totp' 
                  ? 'Enter the code from your authenticator app'
                  : 'Enter one of your backup codes'
                }
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4 bg-red-500/10 border-red-500/50">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'totp' ? (
              <>
                <div className="flex justify-center">
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={code}
                    onChange={handleCodeChange}
                    placeholder="000000"
                    className="text-center text-2xl tracking-widest w-40 font-mono bg-white/10 border-white/20 text-white placeholder-gray-500"
                    autoFocus
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="trust-device"
                    checked={trustDevice}
                    onCheckedChange={setTrustDevice}
                    className="border-white/30 data-[state=checked]:bg-purple-600"
                  />
                  <label
                    htmlFor="trust-device"
                    className="text-sm text-gray-300 cursor-pointer"
                  >
                    Trust this device for 30 days
                  </label>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Input
                  type="text"
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="XXXX-XXXX"
                  className="text-center font-mono bg-white/10 border-white/20 text-white placeholder-gray-500"
                  autoFocus
                />
                <p className="text-xs text-gray-400 text-center">
                  Each backup code can only be used once
                </p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || (mode === 'totp' ? code.length !== 6 : code.length < 8)}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {loading ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
              ) : (
                'Verify'
              )}
            </Button>
          </form>

          <div className="mt-4 pt-4 border-t border-white/10">
            {mode === 'totp' ? (
              <button
                onClick={() => { setMode('backup'); setCode(''); setError(''); }}
                className="w-full flex items-center justify-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                <Key className="w-4 h-4" />
                Use a backup code instead
              </button>
            ) : (
              <button
                onClick={() => { setMode('totp'); setCode(''); setError(''); }}
                className="w-full flex items-center justify-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to authenticator code
              </button>
            )}
          </div>

          {onCancel && (
            <button
              onClick={onCancel}
              className="w-full mt-4 text-sm text-gray-500 hover:text-gray-300 transition-colors"
            >
              Cancel and sign in with a different account
            </button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MFAVerify;
